import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SignUpButton } from '@clerk/clerk-react';
import { getExperimentVariant, trackExperimentEvent } from '../utils/abTesting';
import { trackEvent, trackFriction, trackIntent } from '../utils/analytics';

export default function MiniGenerator() {
  const { t, i18n } = useTranslation();
  const badgeVariant = getExperimentVariant('MINI_BADGE', ['A', 'B']);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('bio');
  const [niche, setNiche] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [hasUsedFree, setHasUsedFree] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallReason, setPaywallReason] = useState('limit'); // 'limit', 'refine', 'save'
  const [copied, setCopied] = useState(false);
  const [fingerprint, setFingerprint] = useState('');
  const [error, setError] = useState('');
  const uiLang = (i18n?.language || 'en').slice(0, 2);
  const legalHref = (slug) => ['en', 'pt', 'es', 'fr', 'de'].includes(uiLang) ? `/${uiLang}/${slug}` : `/${slug}`;
  const consentCopy = {
    en: { labelBefore: 'I agree to the', terms: 'Terms of Service', privacy: 'Privacy Policy', refund: 'Refund Policy', labelMiddle: 'and understand the billing and cancellation terms.', error: 'You must accept the legal terms before continuing.' },
    pt: { labelBefore: 'Eu concordo com os', terms: 'Termos de Serviço', privacy: 'Política de Privacidade', refund: 'Política de Reembolso', labelMiddle: 'e compreendo as condições de cobrança e cancelamento.', error: 'Você precisa aceitar os termos legais antes de continuar.' },
    es: { labelBefore: 'Acepto los', terms: 'Términos del Servicio', privacy: 'Política de Privacidad', refund: 'Política de Reembolso', labelMiddle: 'y entiendo las condiciones de facturación y cancelación.', error: 'Debes aceptar los términos legales antes de continuar.' },
    fr: { labelBefore: 'J’accepte les', terms: 'Conditions d’utilisation', privacy: 'Politique de confidentialité', refund: 'Politique de remboursement', labelMiddle: 'et je comprends les conditions de facturation et d’annulation.', error: 'Vous devez accepter les documents juridiques avant de continuer.' },
    de: { labelBefore: 'Ich stimme den', terms: 'Nutzungsbedingungen', privacy: 'Datenschutzrichtlinie', refund: 'Rückerstattungsrichtlinie', labelMiddle: 'zu und verstehe die Bedingungen zu Abrechnung und Kündigung.', error: 'Du musst die rechtlichen Dokumente akzeptieren, bevor du fortfährst.' }
  }[uiLang] || { labelBefore: 'I agree to the', terms: 'Terms of Service', privacy: 'Privacy Policy', refund: 'Refund Policy', labelMiddle: 'and understand the billing and cancellation terms.', error: 'You must accept the legal terms before continuing.' };
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentError, setConsentError] = useState('');

  useEffect(() => {
    trackEvent('mini_gen_page_view');
  }, []);

  useEffect(() => {
    if (showPaywall) {
      trackEvent('upgrade_modal_view', { source: 'MiniGenerator', reason: paywallReason });
      trackExperimentEvent('MINI_BADGE', badgeVariant, 'view', {
        reason: paywallReason,
        source: 'MiniGeneratorPaywall'
      });
    }
  }, [showPaywall, badgeVariant, paywallReason]);

  const getFingerprint = () => {
    // ... logic remains same
    let fp = localStorage.getItem('cs_anon_fp');
    if (!fp) {
      const components = [
        navigator.userAgent,
        navigator.language,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        window.screen.width,
        window.screen.height,
        new Date().getTimezoneOffset(),
      ].join('|');
      let hash = 0;
      for (let i = 0; i < components.length; i += 1) {
        hash = (Math.imul(31, hash) + components.charCodeAt(i)) | 0;
      }
      fp = `fp_${Math.abs(hash)}_${Date.now().toString(36)}`;
      localStorage.setItem('cs_anon_fp', fp);
    }
    return fp;
  };

  const getFreeLimit = () => parseInt(import.meta.env.VITE_ANON_FREE_LIMIT || '2', 10);
  const getCurrentCount = () => parseInt(localStorage.getItem('cs_anon_count') || '0', 10);

  const getCurrentLang = () => {
    const pathParts = window.location.pathname.split('/');
    const explicitRouteLang = pathParts.find((p) => ['en', 'pt', 'es', 'fr', 'de'].includes(p));
    const appLang = (i18n.resolvedLanguage || i18n.language || document.documentElement.lang || 'en').slice(0, 2);
    return explicitRouteLang || appLang || 'en';
  };

  useEffect(() => {
    setFingerprint(getFingerprint());
    setHasUsedFree(getCurrentCount() >= getFreeLimit());
  }, []);

  const triggerPaywall = (reason) => {
    setPaywallReason(reason);
    setShowPaywall(true);
    trackFriction('limit_reached', reason, { source: 'MiniGenerator' });
  };

  useEffect(() => {
    if (!showPaywall) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showPaywall]);

  const handleGenerate = async () => {
    if (!niche.trim() || loading) return;

    const limit = getFreeLimit();
    const currentCount = getCurrentCount();

    if (currentCount >= limit) {
      setHasUsedFree(true);
      triggerPaywall('limit');
      return;
    }

    setLoading(true);
    setError('');
    trackEvent('mini_gen_start', { type: activeTab });

    try {
      const effectiveFingerprint = fingerprint || getFingerprint();
      if (!fingerprint) setFingerprint(effectiveFingerprint);

      const res = await fetch('/api/generate-anon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: activeTab,
          niche: niche.trim(),
          lang: getCurrentLang(),
          fingerprint: effectiveFingerprint,
        }),
      });

      if (res.status === 429) {
        setHasUsedFree(true);
        triggerPaywall('limit');
        localStorage.setItem('cs_anon_count', limit.toString());
        return;
      }

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.error || t('common.error', { defaultValue: 'Something went wrong. Please try again.' }));
        return;
      }

      const newCount = currentCount + 1;
      localStorage.setItem('cs_anon_count', newCount.toString());
      setHasUsedFree(newCount >= limit);
      setResult(data.result || '');
      trackEvent('mini_gen_success', { type: activeTab });
    } catch (e) {
      console.error(e);
      setError(t('common.error', { defaultValue: 'Connection error. Please check your internet.' }));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      trackEvent('mini_gen_copy');
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      console.error(e);
    }
  };

  const handleReset = () => {
    if (getCurrentCount() >= getFreeLimit()) {
      setHasUsedFree(true);
      triggerPaywall('limit');
      return;
    }
    setResult('');
    setError('');
    setCopied(false);
  };

  const remaining = Math.max(0, getFreeLimit() - getCurrentCount());

  const handleUpgradeClick = (plan) => {
    if (!consentAccepted) {
      setConsentError(consentCopy.error);
      return;
    }

    setConsentError('');
    trackIntent(plan, 'checkout_click', { source: 'MiniGenerator', reason: paywallReason });
    if (plan === 'plus') {
      trackExperimentEvent('MINI_BADGE', badgeVariant, 'checkout_click', {
        reason: paywallReason
      });
    }
    sessionStorage.setItem('pending_checkout', JSON.stringify({ planType: plan, billing: 'monthly', consentAccepted: true, consentVersion: '2026-04-legal', consentTimestamp: new Date().toISOString() }));
    navigate(`/sign-up?checkout=${plan}&billing=monthly`);
  };

  return (
    <section className="mini-gen-section">
      {showPaywall && (
        <>
          <div className="mini-gen-paywall-backdrop" onClick={() => setShowPaywall(false)} />
          <div className="mini-gen-paywall-dialog" role="dialog" aria-modal="true">
            <div className="paywall-header">
              <button
                type="button"
                className="paywall-close-btn"
                onClick={() => setShowPaywall(false)}
                aria-label={t('common.close', { defaultValue: 'Close' })}
              >
                ×
              </button>

              <div className="paywall-social-proof">
                {t('landing.mini.socialProof', { defaultValue: 'Trusted by 10,000+ creators worldwide' })}
              </div>

              <h3>{t(`landing.mini.paywallContexts.${paywallReason}.title`, { 
                defaultValue: t('landing.mini.paywallTitleA') 
              })}</h3>
              <p className="paywall-sub">
                {t(`landing.mini.paywallContexts.${paywallReason}.sub`, { 
                  defaultValue: t('landing.mini.paywallSubA') 
                })}
              </p>
            </div>

            <div className="paywall-body">
              <div className="bridge-options">
                <div className="bridge-item">
                  <div className="bridge-info">
                    <span className="bridge-icon">🚀</span>
                    <div className="bridge-text">
                      <strong>{t('landing.mini.bridgeFreeTitle', { defaultValue: 'Sign Up Free' })}</strong>
                      <span>{t('landing.mini.bridgeFreeDesc', { defaultValue: 'Use the basic generator with your free account.' })}</span>
                    </div>
                  </div>
                  <SignUpButton mode="modal">
                    <button type="button" className="bridge-cta" onClick={() => trackIntent('free_signup', 'interact', { source: 'MiniGenerator', reason: paywallReason })}>
                      {t('landing.pricing.free.cta', { defaultValue: 'Start Free' })}
                    </button>
                  </SignUpButton>
                </div>

                <div className="bridge-item featured">
                  <div className="bridge-badge">
                    {badgeVariant === 'B' 
                      ? t('landing.mini.ui.recommended_alt', { defaultValue: 'BEST VALUE' }) 
                      : t('landing.mini.ui.recommended', { defaultValue: 'RECOMMENDED' })}
                  </div>
                  <div className="bridge-info">
                    <span className="bridge-icon">✦</span>
                    <div className="bridge-text">
                      <strong>{t('landing.mini.bridgePlusTitle', { defaultValue: 'Plus Plan' })}</strong>
                      <span>{t('landing.mini.bridgePlusDesc', { defaultValue: 'Unlock Refine, Variations and all current languages.' })}</span>
                    </div>
                  </div>
                  <button type="button" className="bridge-cta primary" onClick={() => handleUpgradeClick('plus')}>
                    {t('landing.pricing.plus.cta', { defaultValue: 'Get Plus' })}
                  </button>
                </div>

                <div className="bridge-item">
                  <div className="bridge-info">
                    <span className="bridge-icon">💎</span>
                    <div className="bridge-text">
                      <strong>{t('landing.mini.bridgeProTitle', { defaultValue: 'Pro Plan' })}</strong>
                      <span>{t('landing.mini.bridgeProDesc', { defaultValue: 'For high volume creation and heavier usage.' })}</span>
                    </div>
                  </div>
                  <button type="button" className="bridge-cta" onClick={() => handleUpgradeClick('pro')}>
                    {t('landing.pricing.pro.cta', { defaultValue: 'Get Pro' })}
                  </button>
                </div>
              </div>
            </div>

            <div className="paywall-footer">
              <div style={{ marginBottom: '0.9rem', textAlign: 'left', fontSize: '0.92rem', lineHeight: 1.6, color: 'var(--muted)' }}>
                <label style={{ display: 'flex', gap: '0.7rem', alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={consentAccepted}
                    onChange={(e) => {
                      setConsentAccepted(e.target.checked);
                      if (e.target.checked) setConsentError('');
                    }}
                    style={{ marginTop: '0.28rem' }}
                  />
                  <span>
                    {consentCopy.labelBefore} <a href={legalHref('terms')} target="_blank" rel="noreferrer">{consentCopy.terms}</a>, <a href={legalHref('privacy')} target="_blank" rel="noreferrer">{consentCopy.privacy}</a> {t('common.and', { defaultValue: 'and' })} <a href={legalHref('refund-policy')} target="_blank" rel="noreferrer">{consentCopy.refund}</a> {consentCopy.labelMiddle}
                  </span>
                </label>
                {consentError ? <div style={{ marginTop: '0.55rem', color: '#fca5a5' }}>{consentError}</div> : null}
              </div>

              <div className="paywall-micro-friction">
                <span>🔒 {t('landing.mini.microFriction1', { defaultValue: 'No card required' })}</span>
                <span>✨ {t('landing.mini.microFriction2', { defaultValue: 'Cancel anytime' })}</span>
              </div>

              <button type="button" className="paywall-back-btn" onClick={() => {
                setShowPaywall(false);
                trackEvent('upgrade_modal_back', { source: 'MiniGenerator', reason: paywallReason });
              }}>
                {t('landing.mini.continueFree', { defaultValue: 'Back to Preview' })}
              </button>
            </div>
          </div>
        </>
      )}

      <div className="mini-gen-container">
        <div className="mini-gen-header">
          <h2>{t('landing.mini.title', { defaultValue: 'Try CaptionScript Free' })}</h2>
          <p>{t('landing.mini.sub', { defaultValue: 'Describe your brand and see the magic.' })}</p>
        </div>

        <div className={`mini-gen-card ${showPaywall ? 'paywall-active' : ''}`}>
          <div className="mini-gen-tabs">
            <button type="button" className={activeTab === 'bio' ? 'active' : ''} onClick={() => {
              setActiveTab('bio');
              trackEvent('mini_gen_tab_switch', { tab: 'bio' });
            }}>
              {t('landing.mini.tabBio', { defaultValue: 'Bio' })}
            </button>
            <button type="button" className={activeTab === 'caption' ? 'active' : ''} onClick={() => {
              setActiveTab('caption');
              trackEvent('mini_gen_tab_switch', { tab: 'caption' });
            }}>
              {t('landing.mini.tabCaption', { defaultValue: 'Caption' })}
            </button>
            <button type="button" className={activeTab === 'hashtags' ? 'active' : ''} onClick={() => {
              setActiveTab('hashtags');
              trackEvent('mini_gen_tab_switch', { tab: 'hashtags' });
            }}>
              {t('landing.mini.tabHashtags', { defaultValue: 'Hashtags' })}
            </button>
          </div>

          <div className="mini-gen-body">
            <textarea
              placeholder={t('landing.mini.placeholder', { defaultValue: 'E.g., sustainable coffee shop in Brooklyn...' })}
              value={niche}
              onChange={(e) => setNiche(e.target.value)}
              disabled={loading}
            />

            {error && (
              <div style={{ color: '#ff6b6b', fontSize: '0.8rem', textAlign: 'center' }}>
                {error}
              </div>
            )}

            {!result && !hasUsedFree && (
              <button type="button" className="btn-primary" onClick={handleGenerate} disabled={loading || !niche.trim()}>
                {loading
                  ? t('landing.mini.generating', { defaultValue: 'Generating...' })
                  : t('landing.mini.generateBtn', { defaultValue: 'Generate Free' })}
              </button>
            )}

            {!result && hasUsedFree && (
              <button type="button" className="btn-primary" onClick={() => triggerPaywall('limit')}>
                {t('landing.mini.ui.limitReachedBtn')}
              </button>
            )}

            {result && (
              <>
                <div className="mini-gen-result">
                  <div className="result-badge">{t('landing.mini.resultBadge', { defaultValue: '✨ PREMIUM PREVIEW' })}</div>
                  <div className="result-text">{result}</div>
                  <div className="result-upsell">
                    {t('landing.mini.postGenerationPreview', { defaultValue: '✦ Result Generated. Unlock Premium to Refine & Save.' })}
                  </div>
                  <div className="result-actions">
                    <button type="button" className="btn-ghost btn-sm" onClick={handleCopy}>
                      {copied
                        ? t('landing.mini.copied', { defaultValue: 'Copied!' })
                        : t('landing.mini.copy', { defaultValue: 'Copy Text' })}
                    </button>
                    <button type="button" className="btn-primary btn-sm" onClick={() => triggerPaywall('refine')}>
                      {t('landing.mini.refineBtn', { defaultValue: 'Refine & Save' })}
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-ghost"
                  onClick={handleReset}
                  style={{ width: '100%', border: '1px solid rgba(196,158,90,0.2)' }}
                >
                  {remaining > 0
                    ? t('landing.mini.generateAnother', { defaultValue: 'Generate Another' })
                    : t('landing.mini.ui.limitReachedBtn')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
